import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
  Button,
} from '@mui/material';
import { X, Plus, FileText, Upload } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { uploadApprovalDocument } from '../../../utils/approvalsApi';

const ATTACHED_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  p: '14px 16px',
  border: '1px solid #000',
  bgcolor: '#fff',
};

const INDEX_SX = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 12,
  color: '#64748B',
  width: 18,
  flexShrink: 0,
};

const FILE_ICON_BOX_SX = {
  width: 28,
  height: 36,
  border: '1px solid #000',
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
};

const DOC_NAME_SX = { fontWeight: 700, fontSize: 14 };
const META_SX = { fontSize: 12, color: '#64748B' };

const REMOVE_BTN_SX = {
  color: '#64748B',
  '&:hover': { color: '#FF0004' },
};

const STACK_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const EMPTY_BOX_SX = {
  p: 4,
  textAlign: 'center',
  border: '1px dashed #A0ABBB',
  bgcolor: '#F8F9FC',
  fontSize: 13,
  color: '#64748B',
};

const ADD_BUTTON_SX = {
  p: '14px 18px',
  border: '1px dashed #000',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  bgcolor: '#fff',
  '&:hover': { bgcolor: '#F8F9FC' },
};

const ADD_CIRCLE_SX = {
  width: 28,
  height: 28,
  border: '1px solid #000',
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
};

const ADD_LABEL_SX = { fontSize: 14, fontWeight: 600 };
const ADD_HINT_SX = { ml: 'auto', fontSize: 12, color: '#64748B' };

const DIALOG_PAPER_SX = {
  border: '1px solid #000',
  borderRadius: 0,
  boxShadow: '0 4px 4px rgba(0,0,0,0.25)',
  maxHeight: '82vh',
};

const DIALOG_HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 3,
  py: 2,
  borderBottom: '1px solid #000',
};

const DIALOG_TITLE_SX = { fontSize: 20, fontWeight: 700 };
const DIALOG_SUBTITLE_SX = { fontSize: 13, color: '#64748B', mt: 0.25 };

const SCOPE_STRIP_SX = {
  bgcolor: '#F8F9FC',
  px: 3,
  py: 1.25,
  borderBottom: '1px solid rgba(0,0,0,0.12)',
  display: 'flex',
  alignItems: 'baseline',
  gap: 1.5,
};

const SCOPE_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: '#64748B',
};

const SCOPE_VALUE_SX = { fontSize: 13, fontWeight: 700, color: '#000' };
const SCOPE_MRN_SX = {
  fontSize: 12,
  color: '#64748B',
  fontFamily: 'ui-monospace, monospace',
};

const TAB_BAR_SX = {
  px: 3,
  borderBottom: '1px solid rgba(0,0,0,0.12)',
};

const TAB_SX = {
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 13,
};

const TAB_PANEL_SX = { p: 3 };

const LIB_SEARCH_SX = { mb: 2 };

const LIB_LIST_WRAP_SX = {
  maxHeight: 320,
  overflowY: 'auto',
  border: '1px solid rgba(0,0,0,0.12)',
};

const LIB_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: 2,
  py: 1.25,
  cursor: 'pointer',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  '&:hover': { bgcolor: '#F8F9FC' },
};

const LIB_NAME_SX = { fontWeight: 700, fontSize: 13 };
const LIB_META_SX = { fontSize: 11, color: '#64748B' };

const LIB_EMPTY_SX = {
  textAlign: 'center',
  color: '#64748B',
  fontSize: 13,
  py: 4,
};

const DROPZONE_SX = (active) => ({
  border: '1px dashed #000',
  bgcolor: active ? '#F8F9FC' : '#fff',
  p: 4,
  textAlign: 'center',
  cursor: 'pointer',
});

const DROP_ICON_WRAP_SX = {
  width: 48,
  height: 48,
  border: '1px solid #000',
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  mx: 'auto',
  mb: 1.5,
};

const DROP_TITLE_SX = { fontSize: 14, fontWeight: 700 };
const DROP_HINT_SX = { fontSize: 12, color: '#64748B', mt: 0.5 };

const LOADING_WRAP_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  py: 4,
};

const SECTION_ERROR_SX = {
  mt: 1,
  fontSize: 12,
  color: '#B42318',
};

const docDisplayName = (doc) => doc?.document_type || doc?.type || doc?.title || 'Document';
const docFilename = (doc) => doc?.filename || doc?.original_filename || doc?.name || '—';
const docPages = (doc) => doc?.pages || doc?.page_count || '—';

const TabPanel = ({ value, index, children }) => {
  if (value !== index) return null;
  return <Box sx={TAB_PANEL_SX}>{children}</Box>;
};

const DocumentAttachWidget = ({
  patientId,
  patientLabel,
  patientMrn,
  documents,
  attachedIds,
  onAttach,
  onRemove,
  loading,
  fieldError,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [localDocs, setLocalDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const mergedDocs = useMemo(
    () => [...documents, ...localDocs],
    [documents, localDocs],
  );

  const attachedDocs = useMemo(
    () => mergedDocs.filter((d) => attachedIds.includes(d.id)),
    [mergedDocs, attachedIds],
  );

  const available = useMemo(
    () => mergedDocs.filter((d) => !attachedIds.includes(d.id)),
    [mergedDocs, attachedIds],
  );

  const filteredAvailable = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter((d) => {
      const fn = (d.filename || d.original_filename || '').toLowerCase();
      const type = (d.document_type || '').toLowerCase();
      return fn.includes(q) || type.includes(q);
    });
  }, [available, search]);

  const handleClose = () => {
    setPickerOpen(false);
    setSearch('');
    setTab(0);
    setUploading(false);
    setUploadError(null);
  };

  const onDrop = async (accepted) => {
    if (!accepted?.length) return;
    if (!patientId) {
      setUploadError('Select a patient before uploading documents.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    let anyError = false;
    for (const file of accepted) {
      try {
        const res = await uploadApprovalDocument({ patientId, file });
        const doc = res.data;
        setLocalDocs((prev) => [...prev, doc]);
        onAttach?.(doc.id);
      } catch {
        anyError = true;
      }
    }
    setUploading(false);
    if (anyError) {
      setUploadError('One or more files failed to upload. Check file size (max 25 MB) and format.');
    } else {
      setTab(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 25 * 1024 * 1024,
    multiple: true,
  });

  const handleAttach = (docId) => {
    onAttach?.(docId);
  };

  return (
    <Box>
      <Box sx={STACK_SX}>
        {attachedDocs.length === 0 ? (
          <Box sx={EMPTY_BOX_SX}>
            No documents attached. Click below to add at least one.
          </Box>
        ) : (
          attachedDocs.map((doc, idx) => (
            <Box key={doc.id} sx={ATTACHED_ROW_SX}>
              <Typography sx={INDEX_SX}>
                {String(idx + 1).padStart(2, '0')}
              </Typography>
              <Box sx={FILE_ICON_BOX_SX}>
                <FileText size={16} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={DOC_NAME_SX} noWrap>
                  {docDisplayName(doc)}
                </Typography>
                <Typography sx={META_SX} noWrap>
                  {docFilename(doc)} · {docPages(doc)} pages
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label={`Remove ${docFilename(doc)}`}
                onClick={() => onRemove?.(doc.id)}
                sx={REMOVE_BTN_SX}
              >
                <X size={16} />
              </IconButton>
            </Box>
          ))
        )}

        <Box
          sx={ADD_BUTTON_SX}
          role="button"
          tabIndex={0}
          onClick={() => setPickerOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPickerOpen(true);
            }
          }}
        >
          <Box sx={ADD_CIRCLE_SX}>
            <Plus size={14} />
          </Box>
          <Typography sx={ADD_LABEL_SX}>Add Document</Typography>
          <Typography sx={ADD_HINT_SX}>From library · Upload</Typography>
        </Box>

        {fieldError && (
          <Typography sx={SECTION_ERROR_SX}>{fieldError}</Typography>
        )}
      </Box>

      <Dialog
        open={pickerOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <Box sx={DIALOG_HEADER_SX}>
          <Box>
            <Typography sx={DIALOG_TITLE_SX}>Add Document</Typography>
            <Typography sx={DIALOG_SUBTITLE_SX}>
              Attach files behind the secure link. Document type names are OK to display.
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={handleClose} size="small">
            <X size={16} />
          </IconButton>
        </Box>

        {patientId && (
          <Box sx={SCOPE_STRIP_SX}>
            <Typography sx={SCOPE_LABEL_SX}>SCOPE</Typography>
            <Typography sx={SCOPE_VALUE_SX}>{patientLabel || 'Selected patient'}</Typography>
            {patientMrn && (
              <Typography sx={SCOPE_MRN_SX}>MRN {patientMrn}</Typography>
            )}
          </Box>
        )}

        <Box sx={TAB_BAR_SX}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            TabIndicatorProps={{ style: { backgroundColor: '#000' } }}
          >
            <Tab label="From Library" sx={TAB_SX} disableRipple />
            <Tab label="Upload" sx={TAB_SX} disableRipple />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <TabPanel value={tab} index={0}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search documents"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={LIB_SEARCH_SX}
            />
            {loading ? (
              <Box sx={LOADING_WRAP_SX}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Box sx={LIB_LIST_WRAP_SX}>
                {filteredAvailable.length === 0 ? (
                  <Typography sx={LIB_EMPTY_SX}>
                    {available.length === 0
                      ? 'All available documents are already attached.'
                      : 'No documents match your search.'}
                  </Typography>
                ) : (
                  filteredAvailable.map((doc) => (
                    <Box
                      key={doc.id}
                      sx={LIB_ROW_SX}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleAttach(doc.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleAttach(doc.id);
                        }
                      }}
                    >
                      <Box sx={FILE_ICON_BOX_SX}>
                        <FileText size={16} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={LIB_NAME_SX} noWrap>
                          {docDisplayName(doc)}
                        </Typography>
                        <Typography sx={LIB_META_SX} noWrap>
                          {docFilename(doc)} · {docPages(doc)} pages
                        </Typography>
                      </Box>
                      <Plus size={16} />
                    </Box>
                  ))
                )}
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tab} index={1}>
            {uploading ? (
              <Box sx={LOADING_WRAP_SX}>
                <CircularProgress size={20} />
                <Typography sx={{ ml: 1.5, fontSize: 13 }}>Uploading…</Typography>
              </Box>
            ) : (
              <Box {...getRootProps()} sx={DROPZONE_SX(isDragActive)}>
                <input {...getInputProps()} />
                <Box sx={DROP_ICON_WRAP_SX}>
                  <Upload size={18} />
                </Box>
                <Typography sx={DROP_TITLE_SX}>
                  Drag and drop files here
                </Typography>
                <Typography sx={DROP_HINT_SX}>
                  or click to browse · PDF, DOCX up to 25 MB each
                </Typography>
              </Box>
            )}
            {uploadError && (
              <Typography sx={SECTION_ERROR_SX}>{uploadError}</Typography>
            )}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleClose} disabled={uploading}>Done</Button>
            </Box>
          </TabPanel>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentAttachWidget;
